import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Bell, BellOff, MailCheck, MailWarning, Camera, ExternalLink } from "lucide-react";
import { useAuth, API_URL } from "../AuthContext";
import ConnectedSites from "./ConnectedSites";
import { SPECIALTIES } from "../SpecialtyContext";
import { isPushSupported, getCurrentSubscription, subscribeToPush, unsubscribeFromPush } from "../pushNotifications";
import AuthorStats from "./AuthorStats";

const ROLE_LABELS = { admin: "Администратор", editor: "Редактор", user: "Пользователь" };

export default function Profile() {
  const { user, token, updateUser, logout } = useAuth();
  const [name, setName] = useState(user?.display_name || "");
  const [specialty, setSpecialty] = useState(user?.specialty || "");
  const [nameSaving, setNameSaving] = useState(false);
  const [nameMsg, setNameMsg] = useState(null);

  const [verifFile, setVerifFile] = useState(null);
  const [verifBusy, setVerifBusy] = useState(false);
  const [verifMsg, setVerifMsg] = useState(null);
  const [verifStatus, setVerifStatus] = useState(user?.verification_status || "none");

  const [curPass, setCurPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [newPass2, setNewPass2] = useState("");
  const [passSaving, setPassSaving] = useState(false);
  const [passMsg, setPassMsg] = useState(null);

  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState(null);

  const [delPass, setDelPass] = useState("");
  const [delConfirm, setDelConfirm] = useState(false);
  const [delBusy, setDelBusy] = useState(false);
  const [delError, setDelError] = useState(null);

  const [resendBusy, setResendBusy] = useState(false);
  const [resendMsg, setResendMsg] = useState(null);

  const [username, setUsername] = useState(user?.username || "");
  const [usernameSaving, setUsernameSaving] = useState(false);
  const [usernameMsg, setUsernameMsg] = useState(null);

  const [bio, setBio] = useState(user?.bio || "");
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || "");
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [directoryVisible, setDirectoryVisible] = useState(user?.directory_visible !== false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);

  const [digestEnabled, setDigestEnabled] = useState(user?.digest_email_enabled !== false);
  const [digestSaving, setDigestSaving] = useState(false);

  useEffect(() => {
    if (!isPushSupported()) return;
    getCurrentSubscription().then(sub => setPushEnabled(!!sub)).catch(() => {});
  }, []);

  const togglePush = async () => {
    setPushBusy(true);
    setPushError(null);
    try {
      if (pushEnabled) {
        await unsubscribeFromPush(token);
        setPushEnabled(false);
      } else {
        await subscribeToPush(token);
        setPushEnabled(true);
      }
    } catch (err) {
      setPushError(err.message);
    } finally {
      setPushBusy(false);
    }
  };

  const deleteAccount = async e => {
    e.preventDefault();
    setDelError(null);
    setDelBusy(true);
    try {
      const r = await fetch(`${API_URL}/auth/me`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_password: delPass || undefined }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || "Не удалось удалить аккаунт");
      logout();
    } catch (err) {
      setDelError(err.message);
    } finally {
      setDelBusy(false);
    }
  };

  if (!user) return null;

  const resendVerification = async () => {
    setResendBusy(true);
    setResendMsg(null);
    try {
      const r = await fetch(`${API_URL}/auth/me/resend-verification`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || "Не удалось отправить письмо");
      setResendMsg({ type: "ok", text: "Письмо отправлено — проверьте почту (и папку «Спам»)" });
    } catch (err) {
      setResendMsg({ type: "err", text: err.message });
    } finally {
      setResendBusy(false);
    }
  };

  const specialtyLabels = (user.specialty || "").split(";").map(s => s.trim()).filter(Boolean)
    .map(id => SPECIALTIES.find(s => s.id === id)?.label || id);

  const saveName = async e => {
    e.preventDefault();
    setNameSaving(true);
    setNameMsg(null);
    try {
      const r = await fetch(`${API_URL}/auth/me`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ display_name: name.trim(), specialty: specialty || null }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Не удалось сохранить");
      updateUser({ display_name: data.display_name, specialty: data.specialty });
      setNameMsg({ type: "ok", text: "Изменения сохранены" });
    } catch (err) {
      setNameMsg({ type: "err", text: err.message });
    } finally {
      setNameSaving(false);
    }
  };

  const uploadAvatar = async file => {
    if (!file) return;
    setAvatarUploading(true);
    setProfileMsg(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await fetch(`${API_URL}/upload/avatar`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || "Не удалось загрузить фото");
      setAvatarUrl(data.url);
    } catch (err) {
      setProfileMsg({ type: "err", text: err.message });
    } finally {
      setAvatarUploading(false);
    }
  };

  const saveProfileCard = async e => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileMsg(null);
    try {
      const r = await fetch(`${API_URL}/auth/me`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          display_name: user.display_name, specialty: user.specialty || null,
          bio: bio.trim() || null, avatar_url: avatarUrl || null, directory_visible: directoryVisible,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Не удалось сохранить");
      updateUser({ bio: data.bio, avatar_url: data.avatar_url, directory_visible: data.directory_visible });
      setProfileMsg({ type: "ok", text: "Профиль-визитка обновлена" });
    } catch (err) {
      setProfileMsg({ type: "err", text: err.message });
    } finally {
      setProfileSaving(false);
    }
  };

  const toggleDigest = async () => {
    const next = !digestEnabled;
    setDigestSaving(true);
    try {
      const r = await fetch(`${API_URL}/auth/me`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ display_name: user.display_name, specialty: user.specialty || null, digest_email_enabled: next }),
      });
      if (r.ok) {
        setDigestEnabled(next);
        updateUser({ digest_email_enabled: next });
      }
    } finally {
      setDigestSaving(false);
    }
  };

  const saveUsername = async e => {
    e.preventDefault();
    setUsernameSaving(true);
    setUsernameMsg(null);
    try {
      const r = await fetch(`${API_URL}/auth/me/username`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ username: username.trim() }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Не удалось сохранить");
      updateUser({ username: data.username });
      setUsername(data.username);
      setUsernameMsg({ type: "ok", text: "Имя пользователя изменено" });
    } catch (err) {
      setUsernameMsg({ type: "err", text: err.message });
    } finally {
      setUsernameSaving(false);
    }
  };

  const submitVerification = async e => {
    e.preventDefault();
    if (!verifFile) return;
    setVerifBusy(true);
    setVerifMsg(null);
    try {
      const form = new FormData();
      form.append("file", verifFile);
      const upload = await fetch(`${API_URL}/upload/verification-doc`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const uploadData = await upload.json();
      if (!upload.ok) throw new Error(uploadData.error || "Не удалось загрузить файл");

      const r = await fetch(`${API_URL}/auth/me/verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ doc_url: uploadData.url }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Не удалось отправить заявку");
      setVerifStatus(data.verification_status);
      setVerifMsg({ type: "ok", text: "Заявка отправлена на проверку администратору" });
    } catch (err) {
      setVerifMsg({ type: "err", text: err.message });
    } finally {
      setVerifBusy(false);
    }
  };

  const savePassword = async e => {
    e.preventDefault();
    setPassMsg(null);
    if (newPass !== newPass2) {
      setPassMsg({ type: "err", text: "Пароли не совпадают" });
      return;
    }
    setPassSaving(true);
    try {
      const r = await fetch(`${API_URL}/auth/me/password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_password: curPass, new_password: newPass }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || "Не удалось сменить пароль");
      setPassMsg({ type: "ok", text: "Пароль изменён" });
      setCurPass(""); setNewPass(""); setNewPass2("");
    } catch (err) {
      setPassMsg({ type: "err", text: err.message });
    } finally {
      setPassSaving(false);
    }
  };

  return (
    <section style={{ maxWidth: 520 }}>
      <h2 className="section-title">Мой профиль</h2>

      <div className="card" style={{ marginBottom: 18 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14 }}>
          <div style={{
            width: 48, height: 48, borderRadius: "50%", background: "#007c84",
            color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
            fontWeight: 700, fontSize: "1.3rem", flexShrink: 0
          }}>
            {(user.display_name || "?")[0].toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: "1.05rem", color: "#007c84" }}>{user.display_name}</div>
            <div style={{ fontSize: "0.85rem", color: "#888" }}>{user.email}</div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
          <span className={`role-badge role-${user.role}`}>{ROLE_LABELS[user.role] || user.role}</span>
          {specialtyLabels.map(label => <span key={label} className="spec-badge">{label}</span>)}
          {verifStatus === "verified" && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#15803d", color: "#fff", borderRadius: 6, padding: "2px 10px", fontSize: "0.78rem", fontWeight: 700 }}>
              <BadgeCheck size={13} strokeWidth={2.4} /> Подтверждённый врач
            </span>
          )}
          {user.oauth_provider && (
            <span style={{ background: "#f0f2f8", color: "#555", borderRadius: 6, padding: "2px 10px", fontSize: "0.78rem" }}>
              Вход через {user.oauth_provider === "yandex" ? "Яндекс" : "Mail.ru"}
            </span>
          )}
        </div>
      </div>

      {/* Подтверждение email */}
      {!user.oauth_provider && (
        <div className="card" style={{ marginBottom: 18 }}>
          <h3 className="admin-section-title" style={{ marginTop: 0 }}>Подтверждение email</h3>
          {user.email_verified ? (
            <p style={{ display: "flex", alignItems: "center", gap: 6, color: "#15803d", fontWeight: 700, fontSize: "0.95rem", margin: 0 }}>
              <MailCheck size={16} strokeWidth={2.4} /> Email подтверждён
            </p>
          ) : (
            <>
              <p style={{ display: "flex", alignItems: "center", gap: 6, color: "#c0392b", fontSize: "0.9rem", marginTop: 0 }}>
                <MailWarning size={16} strokeWidth={2.4} /> Email не подтверждён — писать в чат нельзя, пока вы не перейдёте по ссылке из письма
              </p>
              {resendMsg && <div className={resendMsg.type === "err" ? "auth-error" : "auth-success"} style={{ marginBottom: 10 }}>{resendMsg.text}</div>}
              <button onClick={resendVerification} disabled={resendBusy} style={{ marginTop: 0 }}>
                {resendBusy ? "Отправка…" : "Отправить письмо ещё раз"}
              </button>
            </>
          )}
        </div>
      )}

      {/* Уведомления: push + email-дайджест */}
      <div className="card" style={{ marginBottom: 18 }}>
        <h3 className="admin-section-title" style={{ marginTop: 0 }}>Уведомления</h3>

        {isPushSupported() && (
          <>
            <p style={{ color: "#666", fontSize: "0.9rem", marginTop: 0, marginBottom: 12 }}>
              Получайте push-уведомления о новых сообщениях в чате, статьях, лекциях,
              событиях и клинических случаях — даже когда сайт закрыт.
            </p>
            {pushError && <div className="auth-error" style={{ marginBottom: 10 }}>{pushError}</div>}
            <button
              onClick={togglePush}
              disabled={pushBusy}
              style={{
                background: pushEnabled ? "#e8f5ee" : "#007c84",
                color: pushEnabled ? "#15803d" : "#fff",
                border: pushEnabled ? "1.5px solid #15803d" : "none",
                borderRadius: 9, padding: "9px 20px", fontSize: "0.95rem", fontWeight: 700,
                cursor: "pointer", marginTop: 0,
                display: "inline-flex", alignItems: "center", gap: 7,
              }}
            >
              {pushBusy
                ? "…"
                : pushEnabled
                  ? <><Bell size={15} strokeWidth={2.2} /> Уведомления включены</>
                  : <><BellOff size={15} strokeWidth={2.2} /> Включить уведомления</>}
            </button>
            <hr style={{ border: "none", borderTop: "1px solid #eee", margin: "16px 0" }} />
          </>
        )}

        <p style={{ color: "#666", fontSize: "0.9rem", marginTop: 0, marginBottom: 10 }}>
          Раз в неделю — короткая ИИ-сводка свежего по вашей специальности на почту.
        </p>
        <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: digestSaving ? "default" : "pointer", fontWeight: 600, fontSize: "0.92rem" }}>
          <input type="checkbox" checked={digestEnabled} onChange={toggleDigest} disabled={digestSaving} />
          {digestSaving ? "Сохраняем…" : "Присылать дайджест недели на email"}
        </label>
      </div>

      {/* Имя и специальность */}
      <div className="card" style={{ marginBottom: 18 }}>
        <h3 className="admin-section-title" style={{ marginTop: 0 }}>Имя и специальность</h3>
        <form onSubmit={saveName} className="auth-form" style={{ gap: 8 }}>
          {nameMsg && <div className={nameMsg.type === "err" ? "auth-error" : "auth-success"}>{nameMsg.text}</div>}
          <label>Отображаемое имя</label>
          <input className="auth-input" value={name} onChange={e => setName(e.target.value)} required />
          <label>Специальность</label>
          <select className="auth-input" value={specialty} onChange={e => setSpecialty(e.target.value)}>
            <option value="">Не выбрана</option>
            {SPECIALTIES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <button type="submit" disabled={nameSaving || !name.trim()} className="auth-submit-btn" style={{ width: "auto", padding: "8px 24px" }}>
            {nameSaving ? "Сохраняем…" : "Сохранить"}
          </button>
        </form>
      </div>

      {/* Публичный профиль-визитка */}
      <div className="card" style={{ marginBottom: 18 }}>
        <h3 className="admin-section-title" style={{ marginTop: 0 }}>Публичный профиль</h3>
        <p style={{ color: "#666", fontSize: "0.88rem", marginTop: 0, marginBottom: 12 }}>
          Видна коллегам в справочнике врачей и на странице ваших публикаций — фото, короткая справка о себе, статистика.
        </p>
        <form onSubmit={saveProfileCard} className="auth-form" style={{ gap: 8 }}>
          {profileMsg && <div className={profileMsg.type === "err" ? "auth-error" : "auth-success"}>{profileMsg.text}</div>}

          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
            {avatarUrl ? (
              <img src={avatarUrl} alt="" style={{ width: 56, height: 56, borderRadius: "50%", objectFit: "cover" }} />
            ) : (
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#007c84", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "1.4rem" }}>
                {(user.display_name || "?")[0].toUpperCase()}
              </div>
            )}
            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", color: "#007c84", fontSize: "0.88rem", fontWeight: 600 }}>
              <Camera size={16} strokeWidth={2.2} />
              {avatarUploading ? "Загрузка…" : "Сменить фото"}
              <input type="file" accept="image/*" style={{ display: "none" }}
                onChange={e => uploadAvatar(e.target.files?.[0])} disabled={avatarUploading} />
            </label>
          </div>

          <label>О себе</label>
          <textarea className="auth-input" rows={3} maxLength={1000} value={bio}
            onChange={e => setBio(e.target.value)} placeholder="Специализация, стаж, интересы — коротко" />

          <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 400, cursor: "pointer" }}>
            <input type="checkbox" checked={directoryVisible} onChange={e => setDirectoryVisible(e.target.checked)} />
            Показывать в справочнике коллег
          </label>

          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 4 }}>
            <button type="submit" disabled={profileSaving} className="auth-submit-btn" style={{ width: "auto", padding: "8px 24px" }}>
              {profileSaving ? "Сохраняем…" : "Сохранить"}
            </button>
            <Link to={`/doctors/${user.id}`} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: "0.85rem", color: "#e5005b", fontWeight: 600 }}>
              Посмотреть как это видят другие <ExternalLink size={13} />
            </Link>
          </div>
        </form>
      </div>

      {/* @username для упоминаний в чате */}
      <div className="card" style={{ marginBottom: 18 }}>
        <h3 className="admin-section-title" style={{ marginTop: 0 }}>Имя пользователя</h3>
        <p style={{ color: "#666", fontSize: "0.88rem", marginTop: 0, marginBottom: 10 }}>
          По этому имени коллеги смогут упомянуть вас в чате через <b>@{username || "..."}</b>
        </p>
        <form onSubmit={saveUsername} className="auth-form" style={{ gap: 8 }}>
          {usernameMsg && <div className={usernameMsg.type === "err" ? "auth-error" : "auth-success"}>{usernameMsg.text}</div>}
          <input
            className="auth-input" value={username}
            onChange={e => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
            placeholder="ivanov_kardio" minLength={3} maxLength={20} required
          />
          <button type="submit" disabled={usernameSaving || username.trim().length < 3 || username === user.username}
            className="auth-submit-btn" style={{ width: "auto", padding: "8px 24px" }}>
            {usernameSaving ? "Сохраняем…" : "Сохранить"}
          </button>
        </form>
      </div>

      {/* Верификация врача */}
      <div className="card" style={{ marginBottom: 18 }}>
        <h3 className="admin-section-title" style={{ marginTop: 0 }}>Подтверждение врача</h3>
        {verifStatus === "verified" && (
          <p style={{ display: "flex", alignItems: "center", gap: 6, color: "#15803d", fontWeight: 700, fontSize: "0.95rem" }}>
            <BadgeCheck size={16} strokeWidth={2.4} /> Аккаунт подтверждён
          </p>
        )}
        {verifStatus === "pending" && (
          <p style={{ color: "#e5005b", fontWeight: 600, fontSize: "0.95rem" }}>Заявка на проверке у администратора</p>
        )}
        {verifStatus === "rejected" && (
          <p style={{ color: "#c0392b", fontSize: "0.9rem" }}>Заявка отклонена. Вы можете отправить новую.</p>
        )}
        {(verifStatus === "none" || verifStatus === "rejected") && (
          <>
            <p style={{ color: "#666", fontSize: "0.9rem", marginTop: 0, marginBottom: 12 }}>
              Загрузите скан/фото диплома или сертификата — после проверки администратором
              на вашем профиле появится бейдж «Подтверждённый врач». Файл доступен только
              администратору и не публикуется.
            </p>
            {verifMsg && <div className={verifMsg.type === "err" ? "auth-error" : "auth-success"} style={{ marginBottom: 10 }}>{verifMsg.text}</div>}
            <form onSubmit={submitVerification} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <input type="file" accept="image/*" onChange={e => setVerifFile(e.target.files[0])} />
              <button type="submit" disabled={verifBusy || !verifFile} style={{ marginTop: 0 }}>
                {verifBusy ? "Отправка…" : "Отправить на проверку"}
              </button>
            </form>
          </>
        )}
      </div>

      {/* Смена пароля */}
      {!user.oauth_provider && (
        <div className="card">
          <h3 className="admin-section-title" style={{ marginTop: 0 }}>Смена пароля</h3>
          <form onSubmit={savePassword} className="auth-form" style={{ gap: 8 }}>
            {passMsg && <div className={passMsg.type === "err" ? "auth-error" : "auth-success"}>{passMsg.text}</div>}
            <label>Текущий пароль</label>
            <input type="password" className="auth-input" value={curPass} onChange={e => setCurPass(e.target.value)} required />
            <label>Новый пароль <span style={{ color: "#888", fontWeight: 400 }}>(минимум 8 символов)</span></label>
            <input type="password" className="auth-input" value={newPass} onChange={e => setNewPass(e.target.value)} required minLength={8} />
            <label>Повторите новый пароль</label>
            <input type="password" className="auth-input" value={newPass2} onChange={e => setNewPass2(e.target.value)} required minLength={8} />
            <button type="submit" disabled={passSaving} className="auth-submit-btn" style={{ width: "auto", padding: "8px 24px" }}>
              {passSaving ? "Сохраняем…" : "Изменить пароль"}
            </button>
          </form>
        </div>
      )}

      <AuthorStats token={token} />

      <ConnectedSites token={token} />

      {/* Удаление аккаунта (152-ФЗ: право на удаление персональных данных) */}
        <div className="card" style={{ marginTop: 18, borderColor: delConfirm ? "#f3c4c4" : undefined }}>
        <h3 className="admin-section-title" style={{ marginTop: 0, color: "#888" }}>Удалить аккаунт</h3>
        <p style={{ color: "#666", fontSize: "0.9rem", marginTop: 0, marginBottom: 12 }}>
          Безвозвратно удаляет ваш аккаунт и связанные с ним персональные данные
          (push-подписки, привязка к комментариям). Опубликованные материалы и сообщения
          в чате останутся видимыми с сохранением отображаемого на момент удаления имени —
          как исторический контент обсуждения, без привязки к вашему аккаунту.
        </p>
        {!delConfirm ? (
          <button onClick={() => setDelConfirm(true)}
            style={{ background: "#d9534f", padding: "9px 20px", marginTop: 0 }}>
            Удалить аккаунт
          </button>
        ) : (
          <form onSubmit={deleteAccount} className="auth-form" style={{ gap: 8 }}>
            {delError && <div className="auth-error">{delError}</div>}
            {!user.oauth_provider && (
              <>
                <label>Подтвердите паролем</label>
                <input type="password" className="auth-input" value={delPass}
                  onChange={e => setDelPass(e.target.value)} required />
              </>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button type="submit" disabled={delBusy}
                style={{ background: "#d9534f", padding: "9px 20px", marginTop: 0 }}>
                {delBusy ? "Удаляем…" : "Подтвердить удаление"}
              </button>
              <button type="button" onClick={() => { setDelConfirm(false); setDelError(null); }}
                style={{ background: "#888", padding: "9px 20px", marginTop: 0 }}>
                Отмена
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
